import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getXpLeaderboard } from '../services/api';
import Pagination from './Pagination';
import Avatar from './Avatar';
import { useMediaQuery } from '../hooks/useMediaQuery'; // Наш хук для определения мобильных
import { Icons } from './Icons'; // Наши новые иконки
import { formatNumber } from '../utils/formatters';
import '../App.css';

// --- Новый компонент для заголовков таблицы ---
const SortableHeader = ({ label, icon, field, currentSort, onSort, isMobile }) => {
    const isActive = currentSort.replace('monthly', '').toLowerCase() === field.toLowerCase();
    return (
        <th onClick={() => onSort(field)} className={isActive ? 'active' : ''}>
            <div className="header-content">
                {icon}
                {/* На мобильных скрываем текст */}
                {!isMobile && <span>{label}</span>}
                {isActive && <span className="sort-arrow">▼</span>}
            </div>
        </th>
    );
};


export default function XpLeaderboard() {
    const [leaderboardData, setLeaderboardData] = useState({ entries: [], pagination: {} });
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState('totalXp');
    const isMobile = useMediaQuery('(max-width: 768px)'); // Определяем размер экрана
    const [timeframe, setTimeframe] = useState('alltime');

    // --- useCallback, чтобы функция не создавалась заново ---
    const fetchData = useCallback(() => {
        setLoading(true);
        getXpLeaderboard(page, sortBy)
            .then(res => setLeaderboardData(res.data))
            .finally(() => setLoading(false));
    }, [page, sortBy]); // Зависит от страницы и сортировки

    useEffect(fetchData, [fetchData]);

    const handleSort = (field) => {
        // Привязываем сортировку к выбранному временному периоду
        const newSortBy = timeframe === 'monthly' ? `monthly${field.charAt(0).toUpperCase() + field.slice(1)}` : field;
        setSortBy(newSortBy);
        setPage(1);
    };
    const handleTimeframeChange = (newTimeframe) => {
        setTimeframe(newTimeframe);
        // При смене периода сбрасываем сортировку на XP по умолчанию
        setSortBy(newTimeframe === 'monthly' ? 'monthlyTotalXp' : 'totalXp');
        setPage(1);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };
    
    return (
        <div className="xp-leaderboard-page">
            <Link to="/" className="back-link">&larr; Назад на главную</Link>
            <h1>🏆 Лидерборд по опыту</h1>
            <div className="timeframe-switcher">
                <button className={timeframe === 'monthly' ? 'active' : ''} onClick={() => handleTimeframeChange('monthly')}>За месяц</button>
                <button className={timeframe === 'alltime' ? 'active' : ''} onClick={() => handleTimeframeChange('alltime')}>За все время</button>
            </div>
            {loading ? <p>Загрузка...</p> : (
                <>
                    <div className="table-container">
                        <table className="xp-leaderboard-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th className="user-col">Пользователь</th>
                                    <SortableHeader label="Ур." field="level" currentSort={sortBy} onSort={handleSort} />
                                    <SortableHeader label="XP" field="totalXp" currentSort={sortBy} onSort={handleSort} />
                                    <SortableHeader label="Войс" icon={Icons.voice} field="voiceMinutes" currentSort={sortBy} onSort={handleSort} isMobile={isMobile} />
                                    <SortableHeader label="Сообщ." icon={Icons.messages} field="messageCount" currentSort={sortBy} onSort={handleSort} isMobile={isMobile} />
                                    <SortableHeader label="Бонусы" icon={Icons.bonus} field="bonusXp" currentSort={sortBy} onSort={handleSort} isMobile={isMobile} />
                                </tr>
                            </thead>
                            <tbody>
                                {leaderboardData.entries.map(entry => {
                                    // Определяем, какое поле соответствует текущей сортировке
                                    const currentSortField = sortBy.replace('monthly', '').toLowerCase();

                                    return (
                                        <tr key={entry.userId}>
                                            <td>#{entry.rank}</td>
                                            <td className="user-col">
                                                <Avatar
                                                    user={{ ...entry.user, id: entry.userId }}
                                                    className="avatar-small"
                                                    xp={entry.totalXp}
                                                />
                                                <span>{entry.user.serverNickname || entry.user.username}</span>
                                            </td>
                                            <td className={currentSortField === 'totalxp' ? 'highlighted-col' : ''}>
                                                {entry.level}
                                            </td>
                                            <td className={currentSortField === 'totalxp' ? 'highlighted-col' : ''}>
                                                {isMobile
                                                    ? formatNumber(timeframe === 'monthly' ? entry.monthlyTotalXp : entry.totalXp)
                                                    : (timeframe === 'monthly' ? entry.monthlyTotalXp : entry.totalXp)}
                                            </td>
                                            <td className={currentSortField === 'voiceminutes' ? 'highlighted-col' : ''}>
                                                {isMobile
                                                    ? `${Math.floor((timeframe === 'monthly' ? entry.monthlyVoiceMinutes : entry.voiceMinutes) / 60)}ч`
                                                    : `${Math.floor((timeframe === 'monthly' ? entry.monthlyVoiceMinutes : entry.voiceMinutes) / 60)} ч ${(timeframe === 'monthly' ? entry.monthlyVoiceMinutes : entry.voiceMinutes) % 60} м`}
                                            </td>
                                            <td className={currentSortField === 'messagecount' ? 'highlighted-col' : ''}>
                                                {isMobile
                                                    ? formatNumber(timeframe === 'monthly' ? entry.monthlyMessageCount : entry.messageCount)
                                                    : (timeframe === 'monthly' ? entry.monthlyMessageCount : entry.messageCount)}
                                            </td>
                                            <td className={currentSortField === 'bonusxp' ? 'highlighted-col' : ''}>
                                                {isMobile
                                                    ? formatNumber(timeframe === 'monthly' ? entry.monthlyBonusXp : entry.bonusXp)
                                                    : (timeframe === 'monthly' ? entry.monthlyBonusXp : entry.bonusXp)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        currentPage={page}
                        totalPages={leaderboardData.pagination.totalPages}
                        onPageChange={handlePageChange}
                    />
                </>
            )}
        </div>
    );
}